import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  IconButton,
  InputAdornment,
  ListItemText,
  Paper,
  CircularProgress,
  Checkbox,
  Menu,
  MenuItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableHead,
  TableSortLabel,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  ContentCopyOutlined,
  PictureAsPdfOutlined,
  FileDownloadOutlined,
  FirstPage as FirstPageIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  LastPage as LastPageIcon,
  SearchRounded as SearchRoundedIcon,
  FilterAlt as FilterAltIcon,
  RestartAlt as RestartAltIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import Breadcrumb from "../../../layouts/full-layout/breadcrumb/Breadcrumb";
import PageContainer from "../../../components/container/PageContainer";
import { GetTenHoursReport } from "../../../services/asai";
import { downloadReportPdf } from "./reportPdf";

type EmployeeType = "payroll" | "contract" | "cnc";

type ReportRow = {
  personId: string;
  personName: string;
  shift: string;
  inTime: string;
  outTime: string;
  status: string;
  hours: string;
  hoursStatus: string;
  overtime: string;
};

type ColumnKey = keyof ReportRow;

const EMPLOYEE_TABS: Array<{
  value: EmployeeType;
  label: string;
}> = [
    { value: "payroll", label: "Payroll Report" },
    { value: "contract", label: "Contract Report" },
    { value: "cnc", label: "Apprentice Report" },
  ];

const PAGE_TITLES: Record<EmployeeType, string> = {
  payroll: "10 Hours Payroll Report",
  contract: "10 Hours Contract Report",
  cnc: "Apprentice Report",
};

const BASE_COLUMNS: Array<{
  key: ColumnKey;
  label: string;
}> = [
    { key: "personId", label: "ID" },
    { key: "personName", label: "Person Name" },
    { key: "shift", label: "Shift" },
    { key: "inTime", label: "In Time" },
    { key: "outTime", label: "Out Time" },
    { key: "status", label: "Status" },
    { key: "hours", label: "Hours" },
    { key: "hoursStatus", label: "Hours Status" },
  ];

const REPORT_COLUMNS: Array<{
  key: ColumnKey;
  label: string;
}> = [
    ...BASE_COLUMNS,
    { key: "overtime", label: "Overtime" },
  ];

const parseRows = (response: any): ReportRow[] => {
  const result =
    response?.data?.data ??
    response?.data ??
    {};

  const records = Array.isArray(result?.result)
    ? result.result
    : Array.isArray(result?.Result)
      ? result.Result
      : [];

  return records.map((item: any) => ({
    personId: String(
      item?.personId ??
      item?.PersonId ??
      item?.personid ??
      "",
    ),
    personName: String(
      item?.personName ??
      item?.PersonName ??
      item?.name ??
      "",
    ),
    shift: String(
      item?.shift ??
      item?.Shift ??
      item?.Organization ??
      "",
    ),
    inTime: String(
      item?.inTime ??
      item?.InTime ??
      item?.checkin ??
      "-",
    ),
    outTime: String(
      item?.outTime ??
      item?.OutTime ??
      item?.checkout ??
      "-",
    ),
    status: String(
      item?.status ??
      item?.Status ??
      "-",
    ),
    hours: String(
      item?.hours ??
      item?.Hours ??
      "-",
    ),
    hoursStatus: String(
      item?.hoursStatus ??
      item?.HoursStatus ??
      item?.gender ??
      "",
    ),
    overtime: String(
      item?.overtime ??
      item?.Overtime ??
      "0.000",
    ),
  }));
};

const getToday = () =>
  new Date().toISOString().slice(0, 10);

function TablePaginationActions(props: {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    page: number,
  ) => void;
}) {
  const { count, page, rowsPerPage, onPageChange } = props;
  const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        size="small"
        onClick={(event) => onPageChange(event, 0)}
        disabled={page === 0}
      >
        <FirstPageIcon fontSize="small" />
      </IconButton>

      <IconButton
        size="small"
        onClick={(event) => onPageChange(event, page - 1)}
        disabled={page === 0}
      >
        <KeyboardArrowLeft fontSize="small" />
      </IconButton>

      <IconButton
        size="small"
        onClick={(event) => onPageChange(event, page + 1)}
        disabled={page >= lastPage}
      >
        <KeyboardArrowRight fontSize="small" />
      </IconButton>

      <IconButton
        size="small"
        onClick={(event) => onPageChange(event, lastPage)}
        disabled={page >= lastPage}
      >
        <LastPageIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

const ReportPage: React.FC = () => {
  const [employeeType, setEmployeeType] =
    useState<EmployeeType>("payroll");

  const [selectedDate, setSelectedDate] =
    useState(getToday());

  const [rows, setRows] =
    useState<ReportRow[]>([]);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  const [tableSearch, setTableSearch] = useState("");

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [hiddenColumns, setHiddenColumns] =
    useState<Set<ColumnKey>>(new Set());

  const [columnMenuAnchorEl, setColumnMenuAnchorEl] =
    useState<null | HTMLElement>(null);

  const [showAllFilters, setShowAllFilters] = useState(false);

  const [columnFilters, setColumnFilters] = useState<
    Partial<Record<ColumnKey, string>>
  >({});

  const [orderDirection, setOrderDirection] =
    useState<"asc" | "desc">("asc");

  const [valueToOrderBy, setValueToOrderBy] =
    useState<ColumnKey>("personId");

  const columns =
    employeeType === "cnc"
      ? BASE_COLUMNS
      : REPORT_COLUMNS;

  const loadReport = React.useCallback(async (
    type: EmployeeType,
    date: string,
  ) => {
    if (!date) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response =
        await GetTenHoursReport({
          employeeType: type,
          date,
        });

      setRows(parseRows(response));
      setPage(0);
    } catch (requestError: any) {
      console.error(
        "Failed to load 10-hour report:",
        requestError,
      );

      setRows([]);
      setError(
        requestError?.response?.data?.message ||
        requestError?.message ||
        "Unable to load the report.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadReport(employeeType, selectedDate);
  }, [employeeType, loadReport]);

  useEffect(() => {
    setHiddenColumns((previous) => {
      const validKeys = new Set(
        columns.map((column) => column.key),
      );

      return new Set(
        Array.from(previous).filter((key) =>
          validKeys.has(key),
        ),
      );
    });
  }, [employeeType]);

  const hasColumnFilters = Object.values(columnFilters).some(
    (value) => String(value ?? "").trim() !== "",
  );

  const columnFilterCount = Object.values(columnFilters).filter(
    (value) => String(value ?? "").trim() !== "",
  ).length;

  const filteredRows = useMemo(() => {
    let data = [...rows];

    const globalSearch = tableSearch.trim().toLowerCase();

    if (globalSearch) {
      data = data.filter((row) =>
        Object.values(row)
          .map((value) => String(value ?? ""))
          .join(" ")
          .toLowerCase()
          .includes(globalSearch),
      );
    }

    Object.entries(columnFilters).forEach(([field, value]) => {
      const search = String(value ?? "").trim().toLowerCase();

      if (!search) return;

      data = data.filter((row) =>
        String(row[field as ColumnKey] ?? "")
          .toLowerCase()
          .includes(search),
      );
    });

    data.sort((first, second) => {
      const firstValue = String(first[valueToOrderBy] ?? "");
      const secondValue = String(second[valueToOrderBy] ?? "");

      const comparison = firstValue.localeCompare(
        secondValue,
        undefined,
        { numeric: true, sensitivity: "base" },
      );

      return orderDirection === "asc" ? comparison : -comparison;
    });

    return data;
  }, [
    rows,
    tableSearch,
    columnFilters,
    valueToOrderBy,
    orderDirection,
  ]);

  const visibleColumns = columns.filter(
    (column) => !hiddenColumns.has(column.key),
  );

  const activeColumnCount = visibleColumns.length;

  const pagedRows = useMemo(
    () =>
      filteredRows.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [filteredRows, page, rowsPerPage],
  );

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(filteredRows.length / rowsPerPage) - 1,
    );

    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredRows.length, page, rowsPerPage]);

  const toggleColumn = (key: ColumnKey) => {
    setHiddenColumns((previous) => {
      const next = new Set(previous);

      if (next.has(key)) {
        next.delete(key);
      } else if (next.size < columns.length - 1) {
        next.add(key);
      }

      return next;
    });
  };

  const handleSort = (field: ColumnKey) => {
    const isAsc =
      valueToOrderBy === field && orderDirection === "asc";

    setOrderDirection(isAsc ? "desc" : "asc");
    setValueToOrderBy(field);
    setPage(0);
  };

  const clearColumnFilter = (field: ColumnKey) => {
    setColumnFilters((previous) => {
      const updated = { ...previous };
      delete updated[field];
      return updated;
    });
    setPage(0);
  };

  const isColumnFiltered = (field: ColumnKey) =>
    Boolean(columnFilters[field]?.trim());

  const resetTableFilters = () => {
    setTableSearch("");
    setColumnFilters({});
    setShowAllFilters(false);
    setPage(0);
  };

  const copyRows = async () => {
    const header =
      visibleColumns
        .map((column) => column.label)
        .join("\t");

    const body = filteredRows
      .map((row) =>
        visibleColumns
          .map((column) =>
            String(row[column.key] ?? ""),
          )
          .join("\t"),
      )
      .join("\n");

    try {
      await navigator.clipboard.writeText(
        `${header}\n${body}`,
      );
    } catch {
      setError(
        "Unable to copy the report to clipboard.",
      );
    }
  };

  const exportExcel = () => {
    const data = filteredRows.map((row) => {
      const record: Record<string, string> = {};

      visibleColumns.forEach((column) => {
        record[column.label] =
          String(row[column.key] ?? "");
      });

      return record;
    });

    const worksheet =
      XLSX.utils.json_to_sheet(data);

    const workbook =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Report",
    );

    XLSX.writeFile(
      workbook,
      `${PAGE_TITLES[employeeType].replace(
        /\s+/g,
        "_",
      )}_${selectedDate}.xlsx`,
    );
  };

  const exportPdf = () => {
    downloadReportPdf({
      title: PAGE_TITLES[employeeType],
      date: selectedDate,
      columns: visibleColumns,
      rows: filteredRows,
      fileName: `${PAGE_TITLES[employeeType].replace(/\s+/g, "_")}_${selectedDate}.pdf`,
    });
  };

  const activeEmployeeLabel =
    EMPLOYEE_TABS.find(
      (tab) => tab.value === employeeType,
    )?.label ?? "";

  const breadcrumbItems = [
    { to: "/", title: "ASAI" },
    { title: "10 Hours Shift Reports" },
    { title: "Report" },
    { title: activeEmployeeLabel },
  ];

  return (
    <PageContainer
      title={PAGE_TITLES[employeeType]}
    >
      <Breadcrumb
        title={PAGE_TITLES[employeeType]}
        items={breadcrumbItems}
      />

      <Box
        sx={{
          width: "100%",
          px: { xs: 1.5, sm: 2, md: 3 },
          py: { xs: 1.5, md: 2.5 },
        }}
      >
        <Card
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid",
              borderColor: "divider",
              px: 2,
            }}
          >
            <Tabs
              value={employeeType}
              onChange={(_, value) => {
                setEmployeeType(value);
                setTableSearch("");
                setColumnFilters({});
                setPage(0);
              }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              {EMPLOYEE_TABS.map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={tab.label}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                />
              ))}
            </Tabs>
          </Box>

          <Box
            sx={{
              p: { xs: 1.5, md: 2.5 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "end",
                justifyContent: "space-between",
                gap: 1.5,
                mb: 2,
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "end",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  label="Date"
                  type="date"
                  value={selectedDate}
                  onChange={(event) => {
                    setSelectedDate(event.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ width: 250 }}
                />

                <Button
                  variant="contained"
                  onClick={() => void loadReport(employeeType, selectedDate)}
                  disabled={loading || !selectedDate}
                  sx={{
                    minHeight: 40,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Search
                </Button>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 0.75,
                  flexWrap: "wrap",
                  ml: "auto",
                }}
              >
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<ContentCopyOutlined />}
                  onClick={() => void copyRows()}
                  disabled={!filteredRows.length}
                  sx={{ textTransform: "none" }}
                >
                  Copy
                </Button>

                <Button
                  size="small"
                  variant="contained"
                  startIcon={<FileDownloadOutlined />}
                  onClick={exportExcel}
                  disabled={!filteredRows.length}
                  sx={{ textTransform: "none" }}
                >
                  Excel
                </Button>

                <Button
                  size="small"
                  variant="contained"
                  startIcon={<PictureAsPdfOutlined />}
                  onClick={exportPdf}
                  disabled={!filteredRows.length}
                  sx={{ textTransform: "none" }}
                >
                  PDF
                </Button>

                <TextField
                  size="small"
                  placeholder="Search..."
                  value={tableSearch}
                  onChange={(event) => {
                    setTableSearch(event.target.value);
                    setPage(0);
                  }}
                  sx={{
                    width: { xs: "100%", md: 200 },
                    backgroundColor: "background.paper",
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <FilterAltIcon
                    sx={{
                      cursor: "pointer",
                      color: showAllFilters
                        ? "primary.main"
                        : "text.primary",
                    }}
                    onClick={() => {
                      setShowAllFilters((current) => !current);
                      setTableSearch("");
                      setPage(0);
                    }}
                  />

                  {hasColumnFilters && !showAllFilters && (
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{
                        textTransform: "none",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        whiteSpace: "nowrap",
                      }}
                      onClick={() => setShowAllFilters(true)}
                    >
                      Filters ({columnFilterCount})
                      <CloseIcon
                        sx={{ fontSize: 16 }}
                        onClick={(event) => {
                          event.stopPropagation();
                          setTableSearch("");
                          setColumnFilters({});
                          setShowAllFilters(false);
                          setPage(0);
                        }}
                      />
                    </Button>
                  )}
                </Box>

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={(event) =>
                    setColumnMenuAnchorEl(event.currentTarget)
                  }
                  sx={{
                    textTransform: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Columns
                  {hiddenColumns.size > 0 && ` (${hiddenColumns.size})`}
                </Button>

                <Menu
                  anchorEl={columnMenuAnchorEl}
                  open={Boolean(columnMenuAnchorEl)}
                  onClose={() => setColumnMenuAnchorEl(null)}
                >
                  {columns.map((column) => (
                    <MenuItem
                      key={column.key}
                      dense
                      onClick={() => toggleColumn(column.key)}
                    >
                      <Checkbox
                        size="small"
                        checked={!hiddenColumns.has(column.key)}
                      />
                      <ListItemText primary={column.label} />
                    </MenuItem>
                  ))}
                </Menu>

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RestartAltIcon />}
                  disabled={!tableSearch.trim() && !hasColumnFilters}
                  onClick={resetTableFilters}
                  sx={{
                    textTransform: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Reset Filter
                </Button>
              </Box>
            </Box>

            {error && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
              >
                {error}
              </Alert>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {PAGE_TITLES[employeeType]}
              </Typography>

              <Typography
                sx={{
                  fontSize: 13,
                  color: "text.secondary",
                }}
              >
                {filteredRows.length} employees
              </Typography>
            </Box>

            {loading ? (
              <Box
                sx={{
                  py: 8,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={30} />
              </Box>
            ) : (
              <>
                <TableContainer
                  component={Paper}
                  sx={{
                    maxHeight: showAllFilters ? 540 : 440,
                    overflowY: filteredRows.length > 5 ? "auto" : "visible",
                    overflowX: "auto",
                    scrollbarGutter: "stable",
                  }}
                >
                  <Table
                    aria-label="10 hour report table"
                    stickyHeader
                    sx={{
                      whiteSpace: "nowrap",
                      minWidth: employeeType === "cnc" ? 950 : 1100,
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        {visibleColumns.map((column) => (
                          <TableCell key={column.key}>
                            <TableSortLabel
                              active={valueToOrderBy === column.key}
                              direction={
                                valueToOrderBy === column.key
                                  ? orderDirection
                                  : "desc"
                              }
                              onClick={() => handleSort(column.key)}
                            >
                              {column.label}
                            </TableSortLabel>
                          </TableCell>
                        ))}
                      </TableRow>

                      {showAllFilters && (
                        <TableRow>
                          {visibleColumns.map((column) => (
                            <TableCell key={column.key}>
                              <TextField
                                size="small"
                                fullWidth
                                placeholder="Search..."
                                value={columnFilters[column.key] || ""}
                                onChange={(event) => {
                                  const value = event.target.value;

                                  setColumnFilters((previous) => {
                                    const updated = { ...previous };

                                    if (value.trim()) {
                                      updated[column.key] = value;
                                    } else {
                                      delete updated[column.key];
                                    }

                                    return updated;
                                  });

                                  setPage(0);
                                }}
                                InputProps={{
                                  endAdornment: isColumnFiltered(column.key) ? (
                                    <InputAdornment position="end">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          clearColumnFilter(column.key)
                                        }
                                      >
                                        <CloseIcon fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  ) : undefined,
                                }}
                              />
                            </TableCell>
                          ))}
                        </TableRow>
                      )}
                    </TableHead>

                    <TableBody>
                      {pagedRows.length ? (
                        pagedRows.map((row, index) => (
                          <TableRow
                            key={`${row.personId}-${row.personName}-${index}`}
                            hover
                          >
                            {visibleColumns.map((column) => (
                              <TableCell key={column.key}>
                                {row[column.key] || "-"}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={Math.max(activeColumnCount, 1)}
                            align="center"
                            sx={{
                              py: 8,
                              color: "text.secondary",
                            }}
                          >
                            {rows.length
                              ? "No records match your filters."
                              : "No records found."}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <TablePagination
                    component="div"
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    count={filteredRows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, value) => setPage(value)}
                    onRowsPerPageChange={(event) => {
                      setRowsPerPage(Number(event.target.value));
                      setPage(0);
                    }}
                    ActionsComponent={TablePaginationActions}
                  />
                </Box>
              </>
            )}
          </Box>
        </Card>
      </Box>
    </PageContainer>
  );
};

export default ReportPage;
